import React, { useState, useEffect, useCallback, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import listPlugin from '@fullcalendar/list';
import '@fullcalendar/common/main.css';
import '@fullcalendar/daygrid/main.css';
import '@fullcalendar/timegrid/main.css';
import '@fullcalendar/list/main.css';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthStore } from '../../auth/state';
import { useNavigate } from 'react-router-dom';
import SharedHeader from '../../components/SharedHeader';
import DashboardFooter from '../../components/DashboardFooter';
import './bookmark_calendar.css';
import { FaTrash, FaTimes, FaExpand } from 'react-icons/fa';
import interact from 'interactjs';

function BookmarkCalendar({ isAdmin }) {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('dayGridMonth');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [draggedEvent, setDraggedEvent] = useState(null);
  const [doubleClickTimeout, setDoubleClickTimeout] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedDates, setSelectedDates] = useState(null);
  const calendarRef = useRef(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, event: null });

  // # Handle body scroll when modal is open
  useEffect(() => {
    if (showEventModal) {
      document.body.classList.add('bmk_cald_modal-open');
    } else {
      document.body.classList.remove('bmk_cald_modal-open');
    }

    return () => {
      document.body.classList.remove('bmk_cald_modal-open');
    };
  }, [showEventModal]);

  // # Initialize drag and drop
  useEffect(() => {
    // # Make events draggable
    interact('.bmk_cald_event-content').draggable({
      inertia: true,
      modifiers: [
        interact.modifiers.restrictRect({
          restriction: 'parent',
          endOnly: true
        })
      ],
      autoScroll: true,
      listeners: {
        start(event) {
          const eventId = event.target.closest('.fc-event').getAttribute('data-event-id');
          const eventData = events.find(e => e.id === eventId);
          setDraggedEvent(eventData);
          event.target.classList.add('bmk_cald_dragging');
        },
        end(event) {
          event.target.classList.remove('bmk_cald_dragging');
          setDraggedEvent(null);
        }
      }
    });

    // # Make calendar cells droppable
    interact('.fc-daygrid-day').dropzone({
      accept: '.bmk_cald_event-content',
      overlap: 0.75,
      ondropactivate: function (event) {
        event.target.classList.add('bmk_cald_drop-active');
      },
      ondragenter: function (event) {
        event.target.classList.add('bmk_cald_drop-target');
      },
      ondragleave: function (event) {
        event.target.classList.remove('bmk_cald_drop-target');
      },
      ondrop: function (event) {
        const date = event.target.getAttribute('data-date');
        if (draggedEvent && date) {
          handleEventDrop(draggedEvent.id, new Date(date));
        }
      },
      ondropdeactivate: function (event) {
        event.target.classList.remove('bmk_cald_drop-active');
        event.target.classList.remove('bmk_cald_drop-target');
      }
    });

    return () => {
      interact('.bmk_cald_event-content').unset();
      interact('.fc-daygrid-day').unset();
    };
  }, [events, draggedEvent]);

  // # Handle event drop
  const handleEventDrop = async (eventId, newDate) => {
    try {
      const event = events.find(e => e.id === eventId);
      if (!event) return;

      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        let updatedData = { ...data };
        const timestamp = new Date().toISOString();

        // # Update the bookmark's schedule based on its type
        if (event.step === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.map(b => 
            b.id === eventId ? { 
              ...b, 
              schedule: { 
                ...b.schedule,
                date: newDate.toISOString() 
              } 
            } : b
          );
        } else if (event.step === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.map(b => 
            b.id === eventId ? { 
              ...b, 
              schedule: { 
                ...b.schedule,
                date: newDate.toISOString() 
              } 
            } : b
          );
        } else if (event.step === 'general') {
          updatedData.generalBookmarks = data.generalBookmarks.map(b => 
            b.id === eventId ? { 
              ...b, 
              schedule: { 
                ...b.schedule,
                date: newDate.toISOString() 
              } 
            } : b
          );
        }

        // # Update metadata
        updatedData.metadata = {
          ...updatedData.metadata,
          lastEditedAt: timestamp
        };

        await updateDoc(bookmarksRef, updatedData);

        // # Update local state
        setEvents(events.map(e => 
          e.id === eventId 
            ? { ...e, start: newDate, end: newDate } 
            : e
        ));
      }
    } catch (error) {
      console.error('Error updating bookmark schedule:', error);
    }
  };

  // # Load scheduled bookmarks
  useEffect(() => {
    const loadScheduledBookmarks = async () => {
      try {
        setLoading(true);
        console.log('Loading scheduled bookmarks for user:', user.uid);
        const startTime = performance.now();
        
        const bookmarksRef = doc(db, 'bookmarks', user.uid);
        const docSnap = await getDoc(bookmarksRef);
        
        const endTime = performance.now();
        console.log('Bookmarks Calendar Firestore read:', {
          userId: user.uid,
          generalCount: docSnap.data()?.generalBookmarks?.length || 0,
          step1Count: docSnap.data()?.step1Bookmarks?.length || 0,
          step2Count: docSnap.data()?.step2Bookmarks?.length || 0,
          readTime: `${(endTime - startTime).toFixed(2)}ms`,
          timestamp: new Date().toISOString()
        });
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const generalEvents = (data.generalBookmarks || [])
            .filter(b => b.schedule?.isScheduled && b.url)
            .map(b => ({
              id: b.id,
              title: b.name,
              start: new Date(b.schedule.date),
              end: new Date(b.schedule.date),
              type: 'General',
              url: b.url,
              step: 'general',
              originalData: b,
              backgroundColor: '#9b59b6',
              borderColor: '#8e44ad',
              classNames: ['bmk_cald_general-event']
            }));

          const step1Events = (data.step1Bookmarks || [])
            .filter(b => b.schedule?.isScheduled)
            .map(b => ({
              id: b.id,
              title: b.topic || b.name,
              start: new Date(b.schedule.date),
              end: new Date(b.schedule.date),
              type: b.type,
              chapter: b.chapter,
              step: 'step1',
              originalData: b,
              backgroundColor: '#3498db',
              borderColor: '#2980b9',
              classNames: ['bmk_cald_step1-event']
            }));

          const step2Events = (data.step2Bookmarks || [])
            .filter(b => b.schedule?.isScheduled)
            .map(b => ({
              id: b.id,
              title: b.topic || b.name,
              start: new Date(b.schedule.date),
              end: new Date(b.schedule.date),
              type: b.type,
              chapter: b.chapter,
              step: 'step2',
              originalData: b,
              backgroundColor: '#e74c3c',
              borderColor: '#c0392b',
              classNames: ['bmk_cald_step2-event']
            }));

            // # Sort events alphabetically with numbers first
            const sortEvents = (events) => {
              return events.sort((a, b) => {
                const titleA = a.title.toLowerCase().trim();
                const titleB = b.title.toLowerCase().trim();
                
                // # Check if both titles start with numbers
                const numA = titleA.match(/^[0-9]+/);
                const numB = titleB.match(/^[0-9]+/);
                
                // # If both start with numbers, compare numerically
                if (numA && numB) {
                  return parseInt(numA[0]) - parseInt(numB[0]);
                }
                
                // # If only one starts with a number, put it first
                if (numA) return -1;
                if (numB) return 1;
                
                // # Otherwise, sort alphabetically
                return titleA.localeCompare(titleB);
              });
            };

            // # Sort each event type separately and combine
            const sortedEvents = [
              ...sortEvents(generalEvents),
              ...sortEvents(step1Events),
              ...sortEvents(step2Events)
            ];

            setEvents(sortedEvents);
        }
      } catch (error) {
        console.error('Error loading scheduled bookmarks:', error);
      } finally {
        setLoading(false);
      }
    };

    if (user?.uid) {
      loadScheduledBookmarks();
    }
  }, [user?.uid]);

  // # Handle event click with double click support
  const handleEventClick = (info) => {
    // # Don't handle clicks if they originated from the delete button
    if (info.jsEvent.target.closest('.bmk_cald_delete-button')) {
      return;
    }

    info.jsEvent.preventDefault();
    const event = info.event;
    const eventData = {
      ...event.extendedProps,
      title: event.title,
      start: event.start,
      end: event.end,
      id: event.id
    };

    // # Check if it's a double click
    if (doubleClickTimeout) {
      clearTimeout(doubleClickTimeout);
      setDoubleClickTimeout(null);
      
      // # Handle double click - navigate to bookmark
      if (eventData.step === 'general') {
        const basePath = isAdmin ? '/admin/bookmarks' : '/bookmarks';
        navigate(`${basePath}/general`);
      } else {
        const basePath = isAdmin ? '/admin/bookmarks' : '/bookmarks';
        navigate(`${basePath}/${eventData.step}/${eventData.chapter}`);
      }
    } else {
      // # Handle single click - show event details
      const timeout = setTimeout(() => {
        setSelectedEvent(eventData);
        setShowEventModal(true);
        setDoubleClickTimeout(null);
      }, 250); // 250ms delay to detect double click
      setDoubleClickTimeout(timeout);
    }
  };

  // # Handle delete event with confirmation dialog
  const handleDelete = async (eventId) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    
    setShowEventModal(false); // Close the event modal
    setDeleteConfirmation({ show: true, event });
  };

  // # Handle confirmed delete
  const handleConfirmDelete = async () => {
    const event = deleteConfirmation.event;
    try {
      const bookmarksRef = doc(db, 'bookmarks', user.uid);
      const docSnap = await getDoc(bookmarksRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        const timestamp = new Date().toISOString();
        let updatedData = { ...data };
        
        if (event.step === 'step1') {
          updatedData.step1Bookmarks = data.step1Bookmarks.filter(b => b.id !== event.id);
        } else if (event.step === 'step2') {
          updatedData.step2Bookmarks = data.step2Bookmarks.filter(b => b.id !== event.id);
        } else if (event.step === 'general') {
          updatedData.generalBookmarks = data.generalBookmarks.filter(b => b.id !== event.id);
        }

        // # Update metadata
        updatedData.metadata = {
          ...updatedData.metadata,
          bookmarksCount: (
            (updatedData.generalBookmarks?.length || 0) +
            (updatedData.step1Bookmarks?.length || 0) +
            (updatedData.step2Bookmarks?.length || 0)
          ),
          lastEditedAt: timestamp
        };

        await updateDoc(bookmarksRef, updatedData);
        setEvents(events.filter(e => e.id !== event.id));
        setShowEventModal(false);
      }
    } catch (error) {
      console.error('Error deleting bookmark:', error);
    } finally {
      setDeleteConfirmation({ show: false, event: null });
    }
  };

  // # Custom event render
  const renderEventContent = (eventInfo) => {
    return (
      <div className="bmk_cald_event-content" data-event-id={eventInfo.event.id} onClick={(e) => e.stopPropagation()}>
        <div className="bmk_cald_event-title">
          {eventInfo.event.title}
        </div>
        <div className="bmk_cald_event-details">
          <span className="bmk_cald_event-type">{eventInfo.event.extendedProps.type}</span>
          {eventInfo.event.extendedProps.chapter && (
            <span className="bmk_cald_event-chapter">{eventInfo.event.extendedProps.chapter}</span>
          )}
        </div>
        <button 
          className="bmk_cald_delete-button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handleDelete(eventInfo.event.id);
          }}
          title="Delete scheduled bookmark"
        >
          <FaTrash />
        </button>
      </div>
    );
  };

  // # Handle ESC key press
  const handleEscKey = useCallback((event) => {
    if (event.key === 'Escape') {
      // Clear any active selection
      if (selectedDates) {
        setSelectedDates(null);
        if (calendarRef.current?.getApi()) {
          calendarRef.current.getApi().unselect();
        }
      }
      // Close modal if open
      if (showEventModal) {
        setShowEventModal(false);
      }
    }
  }, [selectedDates, showEventModal]);

  // # Add event listener for ESC key
  useEffect(() => {
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [handleEscKey]);

  // # Handle date selection
  const handleDateSelect = useCallback((selectInfo) => {
    setSelectedDates({
      start: selectInfo.start,
      end: selectInfo.end
    });
  }, []);

  // # Handle click outside selection
  const handleDateClick = useCallback((arg) => {
    // Clear selection if clicking outside selected range
    if (selectedDates) {
      setSelectedDates(null);
      if (calendarRef.current?.getApi()) {
        calendarRef.current.getApi().unselect();
      }
    }
  }, [selectedDates]);

  return (
    <div className="bmk_cald_calendar-container">
      <SharedHeader />
      
      <div className="bmk_cald_calendar-main-content">
        <div className="bmk_cald_calendar-header">
          <h1>Scheduled Bookmarks</h1>
          <div className="bmk_cald_view-controls">
            <button 
              className={`bmk_cald_view-button ${view === 'dayGridMonth' ? 'bmk_cald_active' : ''}`}
              onClick={() => setView('dayGridMonth')}
            >
              Month
            </button>
            <button 
              className={`bmk_cald_view-button ${view === 'timeGridWeek' ? 'bmk_cald_active' : ''}`}
              onClick={() => setView('timeGridWeek')}
            >
              Week
            </button>
            <button 
              className={`bmk_cald_view-button ${view === 'timeGridDay' ? 'bmk_cald_active' : ''}`}
              onClick={() => setView('timeGridDay')}
            >
              Day
            </button>
            <button 
              className={`bmk_cald_view-button ${view === 'listWeek' ? 'bmk_cald_active' : ''}`}
              onClick={() => setView('listWeek')}
            >
              List
            </button>
          </div>
        </div>

        {loading ? (
          <div className="bmk_cald_loading">Loading scheduled bookmarks...</div>
        ) : (
          <div className="bmk_cald_calendar-wrapper">
            <FullCalendar
              ref={calendarRef}
              plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin]}
              initialView={view}
              headerToolbar={{
                left: 'prev,next today',
                center: 'title',
                right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek'
              }}
              events={events}
              eventContent={renderEventContent}
              eventClick={handleEventClick}
              height="auto"
              aspectRatio={1.8}
              editable={true}
              droppable={true}
              selectable={true}
              selectMirror={true}
              dayMaxEvents={false}
              weekends={true}
              select={handleDateSelect}
              dateClick={handleDateClick}
              unselect={() => setSelectedDates(null)}
              selectOverlap={false}
              selectConstraint={{
                start: '00:00',
                end: '24:00',
                dows: [0, 1, 2, 3, 4, 5, 6]
              }}
              eventTimeFormat={{
                hour: '2-digit',
                minute: '2-digit',
                meridiem: true
              }}
              dayMaxEventRows={false}
              views={{
                dayGridMonth: {
                  dayMaxEventRows: false,
                  moreLinkClick: 'popover'
                },
                timeGridWeek: {
                  dayMaxEventRows: false
                },
                timeGridDay: {
                  dayMaxEventRows: false
                }
              }}
              moreLinkContent={({num}) => `+${num} more`}
              contentHeight="auto"
              stickyHeaderDates={true}
              eventDrop={(info) => {
                handleEventDrop(info.event.id, info.event.start);
              }}
            />
          </div>
        )}

        {/* Event Details Modal */}
        {showEventModal && selectedEvent && (
          <div className="bmk_cald_modal-overlay" onClick={() => setShowEventModal(false)}>
            <div className="bmk_cald_modal-content" onClick={e => e.stopPropagation()}>
              <div className="bmk_cald_modal-header">
                <h3>{selectedEvent.title}</h3>
                <button 
                  className="bmk_cald_modal-close"
                  onClick={() => setShowEventModal(false)}
                >
                  <FaTimes />
                </button>
              </div>
              <div className="bmk_cald_modal-body">
                <p><strong>Type:</strong> {selectedEvent.type}</p>
                {selectedEvent.chapter && (
                  <p><strong>Chapter:</strong> {selectedEvent.chapter}</p>
                )}
                <p><strong>Scheduled for:</strong> {new Date(selectedEvent.start).toLocaleString()}</p>
                {selectedEvent.url && (
                  <p><strong>URL:</strong> <a href={selectedEvent.url} target="_blank" rel="noopener noreferrer">{selectedEvent.url}</a></p>
                )}
              </div>
              <div className="bmk_cald_modal-footer">
                <button 
                  className="bmk_cald_modal-delete"
                  onClick={() => handleDelete(selectedEvent.id)}
                >
                  <FaTrash /> Delete
                </button>
                <button 
                  className="bmk_cald_modal-visit"
                  onClick={() => {
                    if (selectedEvent.step === 'general') {
                      const basePath = isAdmin ? '/admin/bookmarks' : '/bookmarks';
                      navigate(`${basePath}/general`);
                    } else {
                      const basePath = isAdmin ? '/admin/bookmarks' : '/bookmarks';
                      navigate(`${basePath}/${selectedEvent.step}/${selectedEvent.chapter}`);
                    }
                    setShowEventModal(false);
                  }}
                >
                  <FaExpand /> Visit Bookmark
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Popup */}
        {deleteConfirmation.show && deleteConfirmation.event && (
          <div className="bmk_cald_delete-popup-overlay">
            <div className="bmk_cald_delete-popup">
              <h3>Delete Scheduled Bookmark</h3>
              <p>
                Are you sure you want to delete this scheduled bookmark?
                <br /><br />
                <strong>Topic:</strong> {deleteConfirmation.event.originalData.topic || deleteConfirmation.event.title}
                <br />
                <strong>Type:</strong> {deleteConfirmation.event.type}
                <br />
                <strong>Scheduled for:</strong> {new Date(deleteConfirmation.event.start).toLocaleDateString()}
              </p>
              <div className="bmk_cald_delete-popup-buttons">
                <button 
                  className="bmk_cald_delete-popup-cancel"
                  onClick={() => setDeleteConfirmation({ show: false, event: null })}
                >
                  Cancel
                </button>
                <button 
                  className="bmk_cald_delete-popup-confirm"
                  onClick={handleConfirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <DashboardFooter />
    </div>
  );
}

export default BookmarkCalendar; 